import React from "react";
import { useTranslation } from "react-i18next";
import { Trash2, BookOpen } from "lucide-react";
import { Deck } from "@/types/linguadeck";

interface DeleteDeckModalProps {
  deck: Deck;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function DeleteDeckModal({ deck, onConfirm, onCancel }: DeleteDeckModalProps) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 200,
        padding: 16,
        backdropFilter: "blur(4px)",
      }}
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div
        style={{
          background: "hsl(var(--bg2))",
          border: "1px solid hsl(var(--border))",
          borderRadius: "var(--radius)",
          padding: 24,
          width: "100%",
          maxWidth: 420,
          maxHeight: "90dvh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0,
          boxShadow: "0 24px 64px rgba(0,0,0,.5)",
          animation: "fadeInScale .18s ease",
        }}
      >
        {/* Icon */}
        <div
          style={{
            width: 64,
            height: 64,
            flexShrink: 0,
            borderRadius: "50%",
            background: "rgba(255,90,138,.12)",
            border: "1px solid rgba(255,90,138,.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 20,
          }}
        >
          <Trash2 className="h-7 w-7 text-destructive" />
        </div>

        {/* Title */}
        <div
          style={{
            fontFamily: "Instrument Serif, serif",
            fontSize: "clamp(18px, 4.5vw, 22px)",
            fontWeight: 600,
            color: "hsl(var(--foreground))",
            marginBottom: 8,
            textAlign: "center",
            wordBreak: "break-word",
          }}
        >
          {t('deleteModal.title')}
        </div>

        {/* Deck preview */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: "hsl(var(--bg3))",
            border: "1px solid hsl(var(--border))",
            borderRadius: "var(--radius-sm)",
            padding: "10px 16px",
            marginBottom: 16,
            width: "100%",
          }}
        >
          <BookOpen className="h-8 w-8 shrink-0 text-muted-foreground" />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "hsl(var(--foreground))", wordBreak: "break-word" }}>
              {deck.title}
            </div>
            <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}>
              {t('deleteModal.cardsInfo', { count: deck.cardCount ?? deck.cards?.length ?? 0, lang: deck.lang?.toUpperCase() })}
            </div>
          </div>
        </div>

        {/* Warning text */}
        <p
          style={{
            fontSize: 13,
            color: "hsl(var(--muted-foreground))",
            textAlign: "center",
            lineHeight: 1.6,
            marginBottom: 24,
          }}
        >
          {t('deleteModal.irreversibleWarning')}
        </p>

        {/* Buttons */}
        <div style={{ display: "flex", gap: 10, width: "100%", flexShrink: 0 }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1,
              minHeight: 44,
              padding: "12px 20px",
              borderRadius: 50,
              border: "1px solid hsl(var(--border))",
              background: "hsl(var(--bg3))",
              color: "hsl(var(--foreground))",
              fontFamily: "DM Sans, sans-serif",
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
              transition: "all .2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "hsl(var(--primary))")}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "hsl(var(--border))")}
          >
            {t('deleteModal.cancel')}
          </button>
          <button
            onClick={onConfirm}
            style={{
              flex: 1,
              minHeight: 44,
              padding: "12px 20px",
              borderRadius: 50,
              border: "none",
              background: "linear-gradient(135deg, #ff5a8a, #e0365a)",
              color: "white",
              fontFamily: "DM Sans, sans-serif",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all .2s",
              boxShadow: "0 4px 16px rgba(255,90,138,.3)",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            {t('deleteModal.delete')}
          </button>
        </div>
      </div>
    </div>
  );
}
